import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiUrl } from "../config";

function useDailyQuote(language) {
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteErr, setQuoteErr] = useState("");

  useEffect(() => {
    let on = true;

    (async () => {
      try {
        setQuoteLoading(true);
        setQuoteErr("");

        const dayKey = new Date().toISOString().slice(0, 10);
        const cacheKey = `quote:${dayKey}:${language}`;
        const cached = localStorage.getItem(cacheKey);

        if (cached) {
          const obj = JSON.parse(cached);
          if (on) setQuote(obj);
          return;
        }

        const r = await fetch(apiUrl("/api/daily-quote"), { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP_${r.status}`);
        const j = await r.json();

        // If language is English, use as is
        if (language === 'en') {
          if (on) setQuote(j);
          localStorage.setItem(cacheKey, JSON.stringify(j));
          return;
        }

        // Otherwise, translate
        const tr = await fetch(apiUrl("/api/translate-quote"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: j.text, target: language })
        });

        const trJson = await tr.json();
        const translated = trJson.translated || j.text;

        // ✅ Debug logs
        console.log("🌍 Language:", language);
        console.log("🌤 Original quote:", j.text);
        console.log("📝 Translation response:", trJson);
        console.log("✅ Final translated text:", translated);

        const translatedQuote = { ...j, text: translated };
        if (on) setQuote(translatedQuote);
        localStorage.setItem(cacheKey, JSON.stringify(translatedQuote));
      } catch (e) {
        if (on) setQuoteErr("Could not load today’s quote.");
        console.error("[DailyQuoteCard] error:", e);
      } finally {
        if (on) setQuoteLoading(false);
      }
    })();

    return () => { on = false; };
  }, [language]);

  return { quote, quoteLoading, quoteErr };
}

export default function DailyQuoteCard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const { quote, quoteLoading, quoteErr } = useDailyQuote(lang);

  return (
    <div className="home-card" style={{ padding: 14 }}>
      <div className="db-quote-title">💡 {t("quote_of_day")}</div>

      {quoteLoading && <div className="db-horo-line muted">{t("loading_quote")}</div>}
      {!quoteLoading && quoteErr && <div className="db-horo-line warn">{t("could_not_load_quote")}</div>}

      {!quoteLoading && quote && (
        <div className="db-quote-card">
          <div className="db-quote-text">“{quote.text}”</div>
          <div className="db-quote-author">— {quote.author || "—"}</div>
        </div>
      )}
    </div>
  );
}